import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig } from '@/mocks/site';

export default function AboutPage() {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    email: siteConfig.email,
    telephone: siteConfig.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteConfig.address,
      addressLocality: '北京',
      addressCountry: 'CN',
    },
    foundingDate: siteConfig.founded,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '91看片网是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '91看片网是一个专业的影视娱乐内容平台，提供最新电影资讯、深度影评、热门剧集推荐、动漫资讯和综艺娱乐内容。我们致力于为影视爱好者打造一站式的内容服务平台。',
        },
      },
      {
        '@type': 'Question',
        name: '91看片网的内容更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的编辑团队每天更新内容，确保读者能够获取最新、最热的影视资讯和深度文章。无论是电影定档、票房数据还是剧集推荐，您都能在第一时间了解到。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系91看片网？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以通过以下方式联系我们：邮箱 ${siteConfig.email}，电话 ${siteConfig.phone}。我们的办公地址位于${siteConfig.address}。此外，您也可以关注我们的社交媒体账号获取最新动态。`,
        },
      },
      {
        '@type': 'Question',
        name: '91看片网的内容是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，91看片网所有内容均免费向读者开放。我们坚持为广大影视爱好者提供优质的免费内容服务。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteConfig.name} | ${siteConfig.tagline}`}
        description={`了解${siteConfig.name}的品牌故事、发展历程和团队介绍。${siteConfig.description}`}
        keywords={`关于我们,${siteConfig.name},影视平台,品牌介绍,联系方式`}
        canonicalUrl={`${siteConfig.url}/about`}
        ogType="website"
        structuredData={[organizationSchema, faqSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 md:px-6 pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mb-16">
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于 {siteConfig.name}
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm leading-relaxed">
                {siteConfig.tagline}
              </p>
            </div>

            <div className="rounded-lg overflow-hidden h-64 md:h-80 mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20media%20office%20workspace%20with%20film%20posters%20on%20wall%2C%20warm%20ambient%20lighting%2C%20creative%20team%20working%2C%20cinematic%20decor%2C%20professional%20environment%2C%20clean%20composition&width=1200&height=600&seq=about-hero&orientation=landscape"
                alt="91看片网团队"
                className="w-full h-full object-cover"
                title="91看片网 - 办公环境"
              />
            </div>

            <div className="prose-custom text-foreground-700 leading-relaxed space-y-5">
              <p className="text-base">
                <strong className="text-foreground-950">{siteConfig.name}</strong>成立于{siteConfig.founded}年，
                是一个专注于影视娱乐内容创作与分享的综合性平台。我们致力于为影视爱好者提供最新、最全、最有深度的影视资讯和内容推荐。
              </p>
              <p className="text-base">
                经过多年的发展，{siteConfig.name}已经汇聚了一批资深的影视编辑和评论人，
                覆盖了电影、电视剧、动漫、综艺等多个内容领域。我们的团队秉持「发现好片，热爱光影」的理念，
                用心为每一位读者精选优质内容。
              </p>
              <p className="text-base">
                截至目前，{siteConfig.name}已累计发布超过
                <strong className="text-primary-600">{siteConfig.stats.articles}</strong>篇原创内容，
                拥有<strong className="text-primary-600">{siteConfig.stats.users}</strong>忠实读者，
                累计阅读量突破<strong className="text-primary-600">{siteConfig.stats.views}</strong>。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-8 text-center">品牌口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { author: '电影爱好者协会', text: '91看片网的影评质量非常高，每次看完电影都会来这里看深度解读。强烈推荐！', stars: 5 },
                { author: '追剧小能手', text: '剧集推荐栏目是我每天的必看内容，帮我找到了好多冷门好剧，太感谢了。', stars: 5 },
                { author: '动画迷阿飞', text: '动漫板块的内容非常专业，对国产动画的报道尤其到位，是我最喜欢的影视平台。', stars: 5 },
                { author: '影视从业者老张', text: '行业观察栏目的分析很透彻，对市场趋势的把握很准，业内口碑很好。', stars: 4 },
              ].map((review, idx) => (
                <div key={idx} className="p-5 rounded-lg bg-white border border-background-200/70">
                  <div className="flex items-center mb-2">
                    {[...Array(review.stars)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm mr-0.5"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{review.text}&rdquo;</p>
                  <span className="text-xs text-foreground-500">— {review.author}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-8 text-center">联系我们</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="p-6 rounded-lg bg-white border border-background-200/70 text-center">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-950 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-600">{siteConfig.email}</p>
              </div>
              <div className="p-6 rounded-lg bg-white border border-background-200/70 text-center">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-phone-line text-xl text-accent-500"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-950 mb-2">联系电话</h3>
                <p className="text-sm text-foreground-600">{siteConfig.phone}</p>
              </div>
              <div className="p-6 rounded-lg bg-white border border-background-200/70 text-center">
                <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-line text-xl text-secondary-500"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-950 mb-2">办公地址</h3>
                <p className="text-sm text-foreground-600">{siteConfig.address}</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-8 text-center">常见问题</h2>
            <div className="space-y-3">
              {faqSchema.mainEntity.map((faq, idx) => (
                <details key={idx} className="group bg-white border border-background-200/70 rounded-lg overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between p-5 font-medium text-foreground-800 text-sm list-none">
                    {faq.name}
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform duration-200 text-foreground-400"></i>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.acceptedAnswer.text}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}